import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isSelected } from 'helpers';
import { useDebounce } from 'hooks';
import useRoleUIsService from 'hooks/services/useRoleUIsService';
import { ruLocale } from 'lang/ruLocale';
import { addLocale, FilterMatchMode, locale } from 'primereact/api';
import { Column } from 'primereact/column';
import {   DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ComplexRoleUI, RoleUI } from 'types/interfaces';
import { clearFilter } from 'utils';
import VisibilityWrapper from 'utils/wrappers/VisibilityWrapper';

import { ComplexRoleService } from 'components/services/ComplexRoleService';
import { ComplexRoleTemplate } from 'constants/ComplexRoleTemplate';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';
import TaskComplexRoleCard from 'ui/TaskComplexRoleCard';

export default function ComplexRole() {
  addLocale('ru', ruLocale.ru);
  locale('ru');

  
  const [complexRoles, setComplexRoles] = useState<ComplexRoleUI[]>([]);
  const [roles, setRoles] = useState<RoleUI[]>([]);
  const [selectedComplexRole, setSelectedComplexRole] = useState<ComplexRoleUI>(ComplexRoleTemplate);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(100);
  const [pageNumber, setPageNumber] = useState(1);
  const [first, setFirst] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);

  const [filters, setFilters] = useState<Record<string, DataTableFilterMetaData>>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    roleUIs: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const nameFilterRef = useRef<HTMLInputElement>(null);
  const descriptionFilterRef = useRef<HTMLInputElement>(null);
  const roleUIsFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const isSelectedIdExist = isSelected(selectedComplexRole);
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);

  const complexRoleCardRef = useRef<null | { handleCancel: () => void }>(null);


  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setMultiSortMeta(e.multiSortMeta ?? null);
  }, []);

  const onPageChanged = useCallback((event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setPageSize(event.rows);
      setFirst(0);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  const loadData = useCallback(() => {
    const sort = multiSortMeta?.[0]; 
    const payload = JSON.stringify([
      { field: 'name', value: filters.name?.value, operator: filters.name?.matchMode },
      { field: 'description', value: filters.description?.value, operator: filters.description?.matchMode },
      { field: 'roleUIs', value: filters.roleUIs?.value, operator: filters.roleUIs?.matchMode }, 
      { field: 'global', value: filters.global?.value, operator: filters.global?.matchMode }
    ]);

    const service = ComplexRoleService.getComplexRoles({
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: payload
    });

    service.then(res => {
      if (res.data.isSuccess) {
        setComplexRoles(res.data.value.itemsList);
        setTotalCount(res.data.value.total || 0);
      }
    });
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const { debouncedCallback: debouncedLoadData } = useDebounce(loadData, 300);

  useEffect(() => {
    debouncedLoadData();
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const roleUIsService = useRoleUIsService();

  useEffect(() => {
    const serviceResult = roleUIsService.getAll({});
    serviceResult.result.then((r) => {
      if (r) {
        setRoles(r.itemsList);
      }
    }).catch(error => {
      console.error('Ошибка при загрузке ролей:', error);
    });
    return () => serviceResult.cancel();
  }, [roleUIsService]);

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const roleUIsBodyTemplate = (rowData: ComplexRoleUI) => {
    if (!rowData.roleUIs || rowData.roleUIs.length === 0) {
      return <span className="text-color-secondary"></span>;
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {rowData.roleUIs.map((role: RoleUI) => (
          <Tag
            key={role.id}
            value={role.name}
            severity="info"
            className="mr-1 mb-1"
            style={{ background: 'rgb(225, 234, 245)', color: '#495057' }}
          />
        ))}
      </div>
    );
  };


  const roleUIsFilterTemplate = (options: any) => {
    return (
      <div className="flex justify-content-end">
        <InputText
          ref={roleUIsFilterRef}
          value={options.value}
          onChange={(e) => {
            const value = e.target.value;
            const _filters = JSON.parse(JSON.stringify(filters));
            _filters['roleUIs'].value = value;
            setFilters(_filters);
          }}
          placeholder="Поиск по названию роли"
        />
      </div>
    );
  };


  const addButtonHandle = () => {
    setSelectedComplexRole(ComplexRoleTemplate);
    setDialogVisible(true);
  };  

  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите роль для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  };

  const deleteButtonHandle = () => {
    if (selectedComplexRole?.id) {
      setDeleteDialogVisible(true);
    }
    if (toast.current && !selectedComplexRole?.id) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите роль для удаления',
        life: 3000
      });
    }
  }

  const header = (  
    <TablePageHeader
      tableName="complexRoles"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  )

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  const onHideDetailComplexRole = () => {
    if (complexRoleCardRef.current !== null) {
      complexRoleCardRef.current.handleCancel();
    }
  };

  const handleUpdate = (id: number, complexRole: ComplexRoleUI) =>
    ComplexRoleService.updateComplexRole(id, complexRole).then(res => {
      loadData();
      return res;
    });

  const handleAdd = (ComplexRole: ComplexRoleUI) =>
    ComplexRoleService.createComplexRole(ComplexRole).then(res => {
      loadData();
      return res;
    });

  const closeDialog = () => {
    setDialogVisible(false);
  };

  return (
    <div className="complex-role-table wrapper wrapper--main">
      <Toast ref={toast} />
 
      <Dialog
        header="Настройка роли"
        visible={dialogVisible}
        style={{ 
          minWidth: '630px', 
          width: '630px',
          height: '500px',
          minHeight: 500 
        }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        maximizable
        resizable 
        modal
        contentStyle={{ 
          height: 'calc(100% - 45px)',
          overflow: 'hidden', 
        }}
        onHide={() => onHideDetailComplexRole()}
      >
        <div style={{ height: '100%'}}>
          <VisibilityWrapper code="taskType-card__roles">
            <TaskComplexRoleCard
              taskComplexRoleCardProps={selectedComplexRole}
              onUpdate={handleUpdate}
              onDelete={id => {
                ComplexRoleService.deleteComplexRole(Number(id)).then(() => loadData());
              }}
              onAdd={handleAdd}
              closeDialog={closeDialog}
              ref={complexRoleCardRef}
              roles={roles}
            />
          </VisibilityWrapper>
        </div>
      </Dialog>    
      
      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={() => {ComplexRoleService.deleteComplexRole(selectedComplexRole.id).then(() => loadData())}}
        entityType="роль"
        entityName={selectedComplexRole?.name}
      />
      
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedComplexRole!}
        onSelectionChange={(e) => setSelectedComplexRole(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedComplexRole(e.data as ComplexRoleUI);
        }}
        metaKeySelection={false}
        scrollable
        paginator
        first={first}
        rows={pageSize}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPage={onPageChanged}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${complexRoles.length > 0 ? totalCount : 0}`}
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={complexRoles}
        dataKey="id"
        filterDisplay="row"
        header={header}
        emptyMessage="Роли не найдены."
        onRowReorder={(e) => setComplexRoles(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
      >
        <Column
          columnKey="name"
          filter
          sortable
          field="name"
          header="Название"
          style={{ minWidth: '1rem', width: '20%' }}
          onFilterClear={() =>
            clearFilter('name', filters, setFilters, nameFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={nameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию"
              />
            </div>
          }
        />
        <Column
          columnKey="description"
          filter
          sortable
          field="description"
          header="Описание"
          style={{ minWidth: '1rem', width: '30%' }}
          onFilterClear={() =>
            clearFilter(
              'description',
              filters,
              setFilters,
              descriptionFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionFilterRef}
                onChange={handleInputChange('description')}
                placeholder="Поиск по описанию"
              />
            </div>
          }
        />
        <Column
          columnKey="roleUIs"
          filter
          sortable
          field="roleUIs"
          header="Набор прав"
          style={{ minWidth: '1rem', width: '50%' }}
          body={roleUIsBodyTemplate}
          filterElement={roleUIsFilterTemplate}
          onFilterClear={() =>
            clearFilter('roleUIs', filters, setFilters, roleUIsFilterRef)
          }
          filterFunction={(value, filter) => {
            if (!filter) return true;
            if (!value || !Array.isArray(value)) return false;
            
            const searchText = filter.toLowerCase();
            return value.some((role: RoleUI) => 
              role.name.toLowerCase().includes(searchText) ||
              role.displayName.toLowerCase().includes(searchText)
            );
          }}
        />
      </DataTable> 
    </div>
  );
}
